import { Delete, Get, Post, Put } from "../api/api";
import { useQuery } from "@tanstack/react-query";
import { Loading } from "../components/Loading";
import { useState, useContext } from "react";
import { Cart } from "../components/Cart";
import { Product } from "../components/Product";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Icon } from "@iconify/react";
import { Menu } from "@szhsin/react-menu";
import { MenuItem } from "@szhsin/react-menu";
import toast from "react-hot-toast";
import { CartContext } from "../context/cart";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";
import { useForm } from "react-hook-form";
import { ProductEditor } from "../components/ProductEditor";

export function Sell() {
  const { cart, setCart } = useContext(CartContext);
  const [order, setOrder] = useState('grouped');
  const [createSection, setCreateSection] = useState(false);
  const [createProductSection, setCreateProductSection] = useState(null);

  const { register: regSection, handleSubmit: submitSectionForm, reset: resetSection } = useForm();

  const { isLoading, data: sections, refetch } = useQuery({
    queryKey: ["sections"],
    queryFn: () => Get({ request: "/sections?shop_id=" + localStorage.getItem("shop_id") }),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });

  if (isLoading) return <Loading />;

  const addToCart = (p) => {
    const product = JSON.parse(JSON.stringify(p));
    setCart(prev => {
      const existing = prev.find(item => item.custom_id == product.id);
      if (existing) {
        return prev.map(item =>
          item.custom_id == product.id ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      return [...prev, { ...product, quantity: 1, custom_price: product.price, custom_mods: [], custom_id: product.id }];
    });
  };

  async function submitSection(values) {
    let t = toast.loading('Creando seccion...');
    let r = await Post({
      request: "/sections?shop_id=" + localStorage.getItem('shop_id'),
      data: { ...values, shop_id: localStorage.getItem('shop_id') }
    });
    if (r.id) {
      toast.success('Seccion creada', { id: t });
      setCreateSection(false);
      resetSection();
    } else {
      toast.error('Error al crear la seccion', { id: t });
    }
    refetch();
  }

  return (
    <>
      <Sheet open={createSection} onOpenChange={setCreateSection}>
        <SheetContent className="w-[500px] sm:max-w-[500px]">
          <SheetHeader><SheetTitle>Crear Seccion</SheetTitle></SheetHeader>
          <form onSubmit={submitSectionForm(submitSection)} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Nombre de la seccion</Label>
              <Input {...regSection("name", { required: true })} />
            </div>
            <div className="flex justify-between">
              <Button type="button" variant="outline" onClick={() => setCreateSection(false)}>Cancelar</Button>
              <Button type="submit" variant="default">Crear Seccion</Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {createProductSection != null && (
        <ProductEditor
          open
          setOpen={(open) => {
            if (!open) setCreateProductSection(null);
          }}
          sectionId={createProductSection}
          refetch={refetch}
        />
      )}

      <div className="flex h-full select-none">
        <div data-bottom-nav-scroll className="space-y-5 p-5 pb-[var(--bottom-nav-reserve)] w-2/3 h-full items-start relative overflow-y-scroll">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-base font-semibold text-neutral-800">Productos</div>
              <div className="text-xs text-neutral-400">Toca un producto para añadirlo</div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex h-12 items-center rounded-xl border border-neutral-200 bg-white p-0.5 shadow-sm">
                <button
                  type="button"
                  onClick={() => setOrder('grouped')}
                  className={`h-11 min-w-20 touch-manipulation rounded-[10px] px-4 text-sm font-medium transition-colors active:scale-[0.98] ${
                    order === 'grouped'
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-neutral-500 hover:bg-neutral-50 hover:text-neutral-800'
                  }`}
                >
                  Grupo
                </button>
                <button
                  type="button"
                  onClick={() => setOrder('listed')}
                  className={`h-11 min-w-20 touch-manipulation rounded-[10px] px-4 text-sm font-medium transition-colors active:scale-[0.98] ${
                    order === 'listed'
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-neutral-500 hover:bg-neutral-50 hover:text-neutral-800'
                  }`}
                >
                  Lista
                </button>
              </div>
              <Button variant="outline" className="h-12 px-4 text-base" onClick={() => setCreateSection(true)}>
                <Icon icon="mdi:plus" className="mr-2 text-xl" />
                Nueva sección
              </Button>
            </div>
          </div>
          {order == 'grouped' && <ProductsGrouped refetch={refetch} sections={sections} addToCart={addToCart} />}
          {order == 'listed' && (
            <ProductsListed
              sections={sections}
              addToCart={addToCart}
              refetch={refetch}
              onCreateProduct={setCreateProductSection}
            />
          )}
        </div>
        <div className="w-1/3">
          <Cart cart={cart} setCart={setCart} />
        </div>
      </div>
    </>
  );
}

function ProductsListed({ sections, addToCart, refetch, onCreateProduct }) {
  return (
    <div className="space-y-8">
      {sections.map((section) => (
        <section key={section.id}>
          <div className="flex items-center justify-between gap-3 border-b border-neutral-100 pb-3">
            <h2 className="text-base font-semibold text-neutral-800">{section.name}</h2>
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-neutral-400">
                {section.products.length} {section.products.length === 1 ? 'producto' : 'productos'}
              </span>
              <button
                type="button"
                aria-label={`Añadir producto a ${section.name}`}
                title="Añadir producto"
                onClick={() => onCreateProduct(section.id)}
                className="flex h-11 w-11 touch-manipulation items-center justify-center rounded-xl border border-neutral-200 bg-white text-blue-600 shadow-sm transition-colors hover:border-blue-200 hover:bg-blue-50 active:scale-95"
              >
                <Icon icon="mdi:plus" className="text-xl" />
              </button>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-5 md:grid-cols-3 lg:grid-cols-4">
            {section.products.map((product) => (
              <Product product={product} key={product.id} addToCart={addToCart} refetch={refetch} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function ProductsGrouped({ sections, addToCart, refetch }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 mt-5">
      {sections.map((section, index) => <Section refetch={refetch} addToCart={addToCart} section={section} key={index} />)}
    </div>
  );
}

function Section({ addToCart, section, refetch }) {
  const [createProduct, setCreateProduct] = useState(false);
  const [editSection, setEditSection] = useState(false);
  const products = section.products;
  const [open, setOpen] = useState(false);
  const confirm = useConfirmDialog();

  const { register: regEdit, handleSubmit: submitEditForm } = useForm({
    defaultValues: { name: section.name }
  });


  function deleteSection() {
    confirm({
      title: 'Eliminar Seccion',
      description: '¿Estas seguro de que quieres eliminar la seccion?',
      okText: 'Eliminar',
      variant: 'danger',
      onOk: async () => {
        await Delete({ request: "/sections/" + section.id + '?shop_id=' + localStorage.getItem('shop_id') });
        refetch();
      },
    });
  }

  async function submitSectionEdit(values) {
    let t = toast.loading('Editando seccion...');
    let r = await Put({
      request: "/sections/" + section.id + '?shop_id=' + localStorage.getItem('shop_id'),
      data: { ...values, shop_id: localStorage.getItem('shop_id') }
    });
    if (r.id) {
      toast.success('Seccion editada', { id: t });
      setEditSection(false);
    } else {
      toast.error('Error al editar la seccion', { id: t });
    }
    refetch();
  }

  return (
    <div>
      {/* Edit Section Sheet */}
      <Sheet open={editSection} onOpenChange={setEditSection}>
        <SheetContent className="w-[500px] sm:max-w-[500px]">
          <SheetHeader><SheetTitle>Editar Seccion</SheetTitle></SheetHeader>
          <form onSubmit={submitEditForm(submitSectionEdit)} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Nombre de la seccion</Label>
              <Input {...regEdit("name", { required: true })} />
            </div>
            <div className="flex justify-between">
              <Button type="button" variant="outline" onClick={() => setEditSection(false)}>Cancelar</Button>
              <Button type="submit" variant="default">Editar Seccion</Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {createProduct && (
        <ProductEditor
          open
          setOpen={setCreateProduct}
          sectionId={section.id}
          refetch={refetch}
        />
      )}

      {/* Section Card */}
      <article className="group relative w-full animate-fade select-none animate-once">
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={`Abrir sección ${section.name}`}
          className="flex min-h-40 w-full touch-manipulation flex-col items-center justify-center rounded-xl border border-neutral-200 bg-white p-4 text-center shadow-sm outline-none transition-all duration-200 hover:-translate-y-0.5 hover:border-blue-200 hover:shadow-md focus-visible:ring-2 focus-visible:ring-blue-500/40 active:scale-[0.98] active:bg-blue-50/40"
        >
          <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-blue-50 text-blue-600 transition-colors group-hover:bg-blue-600 group-hover:text-white">
            <Icon icon="mdi:folder-outline" className="text-4xl" />
          </span>
          <span className="mt-3 line-clamp-2 w-full text-base font-semibold text-neutral-800">
            {section.name}
          </span>
          <span className="mt-1 text-xs font-medium text-neutral-400">
            {products.length} {products.length === 1 ? 'producto' : 'productos'}
          </span>
        </button>

        <div className="absolute right-1.5 top-1.5 z-20">
          <Menu menuButton={
            <button
              type="button"
              aria-label={`Opciones de la sección ${section.name}`}
              className="flex h-11 w-11 touch-manipulation items-center justify-center rounded-xl text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-800 active:scale-95"
            >
              <Icon icon="mdi:dots-vertical" className="text-xl" />
            </button>
          }>
            <MenuItem onClick={() => setCreateProduct(true)}>
              <span className="flex items-center gap-2">
                <Icon icon="mdi:plus" className="text-lg" />
                Añadir producto
              </span>
            </MenuItem>
            <MenuItem onClick={() => setEditSection(true)}>Editar</MenuItem>
            <MenuItem onClick={deleteSection} className='text-red-500'>Eliminar</MenuItem>
          </Menu>
        </div>
      </article>

      {/* Expanded Section. Needs z-30: the section cards and their three-dot menus
          sit at z-20 and, being inside a scroll column without a stacking
          context, they used to float on top of this panel. It stays below the
          bottom nav (z-40) and the sheets/dialogs (z-50). */}
      <div className={`fixed bottom-0 left-0 right-1/3 top-10 z-30 overflow-auto bg-white p-5 pb-[calc(1.25rem_+_var(--bottom-nav-reserve))] ${!open && 'hidden'}`}>
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-1 text-sm">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="-ml-3 inline-flex h-11 touch-manipulation items-center gap-2 rounded-xl px-3 font-semibold text-blue-600 transition-colors hover:bg-blue-50 active:scale-[0.98]"
            >
              <Icon icon="mdi:chevron-left" className="text-2xl" />
              Productos
            </button>
            <Icon icon="mdi:chevron-right" className="text-neutral-300" />
            <span className="truncate text-base font-semibold text-neutral-800">{section.name}</span>
          </div>
          <Button variant="outline" className="h-12 shrink-0 px-4 text-base" onClick={() => setCreateProduct(true)}>
            <Icon icon="mdi:plus" className="mr-2 text-xl" />
            Nuevo producto
          </Button>
        </div>
        <div className="mt-5 grid grid-cols-2 gap-5 md:grid-cols-3 lg:grid-cols-4">
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="flex min-h-48 touch-manipulation flex-col items-center justify-center rounded-xl border border-neutral-200 bg-neutral-50 p-4 text-center shadow-sm transition-all duration-200 hover:border-blue-200 hover:bg-blue-50/60 hover:shadow-md active:scale-[0.98]"
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-white text-blue-600 shadow-sm">
              <Icon icon="mdi:chevron-double-left" className="text-2xl" />
            </span>
            <span className="mt-3 text-base font-semibold text-neutral-800">Volver</span>
            <span className="mt-1 text-xs text-neutral-400">Elegir otra sección</span>
          </button>
          {products.map((product) => <Product product={product} key={product.id} refetch={refetch} addToCart={addToCart} />)}
        </div>
      </div>
    </div>
  );
}
